import {
  AlertTriangle,
  CheckCircle,
  Info,
  TrendingUp,
  XCircle,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CoverageMentionSchema } from '@/services/types/coverage-types';

interface MentionOverviewProps {
  mention: CoverageMentionSchema;
}

export const MentionOverview = ({ mention }: MentionOverviewProps) => {
  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'ACCEPTED':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'REJECTED':
      case 'ERROR':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'READY':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'PROCESSING':
        return <TrendingUp className="h-5 w-5 text-blue-600" />;
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string | null) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      READY: 'bg-green-100 text-green-800',
      ACCEPTED: 'bg-emerald-100 text-emerald-800',
      REJECTED: 'bg-red-100 text-red-800',
      ERROR: 'bg-red-100 text-red-800',
    };
    return colors[status || 'UNKNOWN'] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryColor = (category: string | null) => {
    const colors: Record<string, string> = {
      FIRST_PARTY: 'bg-blue-100 text-blue-800',
      FIRST_PARTY_HEALTH: 'bg-green-100 text-green-800',
      THIRD_PARTY: 'bg-purple-100 text-purple-800',
    };
    return colors[category || 'UNKNOWN'] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Status and Basic Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            {getStatusIcon(mention.status)}
            Processing Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Badge variant="secondary" className={getStatusColor(mention.status)}>
            {mention.status || 'Unknown'}
          </Badge>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Artifact Type:</span>
              <span className="font-medium">{mention.artifact_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Artifact ID:</span>
              <span className="font-mono text-xs">
                {mention.artifact_id.slice(-12)}
              </span>
            </div>
          </div>

          {mention.validation?.is_valid_coverage_mention !== undefined && (
            <div className="flex items-center gap-2 mt-3">
              {mention.validation.is_valid_coverage_mention ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600" />
              )}
              <span className="text-sm">
                {mention.validation.is_valid_coverage_mention
                  ? 'Valid Coverage'
                  : 'Invalid Coverage'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extraction Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Extraction Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {mention.extraction ? (
            <>
              {mention.extraction.category && (
                <Badge
                  variant="secondary"
                  className={getCategoryColor(mention.extraction.category)}
                >
                  {mention.extraction.category.replace('_', ' ')}
                </Badge>
              )}

              <div className="space-y-2 text-sm">
                {mention.extraction.carrier?.name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Carrier:</span>
                    <span className="font-medium">
                      {mention.extraction.carrier.name}
                    </span>
                  </div>
                )}

                {mention.extraction.policy_type && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Type:</span>
                    <span className="text-sm">
                      {mention.extraction.policy_type}
                    </span>
                  </div>
                )}

                {mention.extraction.policy_identifiers &&
                  mention.extraction.policy_identifiers.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Policy:</span>
                      <div className="text-right">
                        {mention.extraction.policy_identifiers.map(
                          (identifier, index) => (
                            <div key={index} className="text-sm">
                              {identifier.identifier_type}:{' '}
                              {identifier.identifier_value}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {mention.extraction.confidence !== undefined && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Confidence:</span>
                    <span className="font-medium">
                      {(mention.extraction.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    value={mention.extraction.confidence * 100}
                    className="h-2"
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-sm">
              No extraction data available
            </p>
          )}
        </CardContent>
      </Card>

      {/* Validation & Scores */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Validation & Scores</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {mention.validation ? (
            <>
              {mention.validation.coverage_specificity_score !== undefined && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Specificity:</span>
                    <span className="font-medium">
                      {(
                        mention.validation.coverage_specificity_score * 100
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                  <Progress
                    value={mention.validation.coverage_specificity_score * 100}
                    className="h-2"
                  />
                </div>
              )}

              {mention.integration?.confidence_score !== undefined && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Integration Confidence:
                    </span>
                    <span className="font-medium">
                      {(mention.integration.confidence_score * 100).toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    value={mention.integration.confidence_score * 100}
                    className="h-2"
                  />
                </div>
              )}

              {mention.integration?.action_recommended && (
                <div className="mt-3">
                  <Badge variant="outline" className="text-xs">
                    {mention.integration.action_recommended.replace('_', ' ')}
                  </Badge>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-sm">
              No validation data available
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
